import React from "react";
import { Container, Row, Col } from "react-bootstrap";
import { Typography } from "@mui/material";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import "./style.css";

type Step = {
  title: string;
  desc: string;
};

type FaceSwapGuideProps = {
  title: string;
  steps: Step[];
  image: string;
};

// Fade Up Animation
const fadeUpVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const FaceSwapGuide: React.FC<FaceSwapGuideProps> = ({
  title,
  steps,
  image,
}) => {
  const { ref: refHowItWorks, inView: inViewHowItWorks } = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });

  return (
    <Container className="my-5 text-center">
      <motion.div
        ref={refHowItWorks}
        initial="hidden"
        animate={inViewHowItWorks ? "visible" : "hidden"}
        variants={fadeUpVariants}
      >
        <h2 className="gradient-text mb-4" style={{ fontSize: "2.2rem", fontWeight: 800 }}>
          {title}
        </h2>
        <div className="glass-panel p-4 p-md-5" style={{ borderRadius: "24px" }}>
          <Row className="align-items-center g-4">
            <Col md={8} className="text-start">
              {steps.map((step, index) => (
                <motion.div key={index} variants={fadeUpVariants}>
                  <div className="d-flex align-items-start mb-4">
                    <div className="step-circle flex-shrink-0">{index + 1}</div>
                    <div className="ms-3">
                      <Typography variant="h6" sx={{ color: "#f8fafc", fontWeight: 700, fontSize: "1.15rem", marginBottom: "4px" }}>
                        {step.title}
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#94a3b8", fontSize: "0.95rem", lineHeight: 1.6 }}>
                        {step.desc}
                      </Typography>
                    </div>
                  </div>
                </motion.div>
              ))}
            </Col>
            <Col md={4} className="text-center">
              <motion.div variants={fadeUpVariants}>
                <div className="image-container">
                  <img
                    src={image}
                    alt="face_swap_image"
                    className="face-image"
                    style={{
                      borderRadius: "16px",
                      border: "2px solid rgba(139, 92, 246, 0.4)",
                      boxShadow: "0 10px 30px rgba(99, 102, 241, 0.3)",
                      maxWidth: "100%",
                      height: "auto",
                    }}
                  />
                </div>
              </motion.div>
            </Col>
          </Row>
        </div>
      </motion.div>
    </Container>
  );
};


const singleFaceSwapSteps = [
  {
    title: "Upload Your Photo",
    desc: "Choose any image from your gallery or take a new one.",
  },
  {
    title: "Select a Face to Swap",
    desc: "Pick the face you want to swap, whether it’s a friend, celebrity, or character.",
  },
  {
    title: "Click the 'swap' button",
    desc: "Our AI-powered face swap technology will seamlessly swap the faces for a realistic and fun transformation.",
  },
  {
    title: "Download & Use",
    desc: "Save your creation and share it on social media, send it to friends, or keep it for laughs!",
  },
];

const multiFaceSwapSteps = [
  {
    title: "Upload a Group Photo",
    desc: "Choose a family photo, couple selfie, or group picture where faces are clearly visible.",
  },
  {
    title: "Let AI Detect Faces",
    desc: "SwapToon’s AI face recognition technology automatically detects and aligns faces for the best swap results.",
  },
  {
    title: "Swap Faces & Customize!",
    desc: "Swap one, two, or all faces—mix and match or try random swaps for a surprise effect!",
  },
  {
    title: "Save & Share the Fun",
    desc: "Download your transformed image and share it on Instagram, WhatsApp, TikTok, or Facebook to surprise your friends!",
  },
];

export const SingleFaceSwapGuide: React.FC = () => (
  <FaceSwapGuide
    title="How to Swap Face Online?"
    steps={singleFaceSwapSteps}
    image="https://snaptoon-development.s3.eu-north-1.amazonaws.com/snaptoon_web/initial_image.jpg"
  />
);
export const MultiFaceSwapGuide: React.FC = () => (
  <FaceSwapGuide
    title="How to Use SwapToon’s Multi-Face Swap?"
    steps={multiFaceSwapSteps}
    image="https://snaptoon-development.s3.eu-north-1.amazonaws.com/snaptoon_web/multi-face-initial.jpg"
  />
);

const preweddingSteps = [
  {
    title: "Upload Pre-Wedding Template Photo",
    desc: "Select a romantic couple portrait, pre-wedding shoot scene, or wedding attire template.",
  },
  {
    title: "AI Detects Couple Faces",
    desc: "SwapToon's AI automatically locates and isolates faces of both the bride and groom.",
  },
  {
    title: "Upload Bride & Groom Faces",
    desc: "Upload photos of yourself and your partner to match each corresponding face slot.",
  },
  {
    title: "Generate & Download Pre-Wedding Photo",
    desc: "Click start swap to create realistic pre-wedding couple portraits ready to share!",
  },
];

export const PreweddingGuide: React.FC = () => (
  <FaceSwapGuide
    title="How to Use Pre-Wedding Face Swap?"
    steps={preweddingSteps}
    image="https://snaptoon-development.s3.eu-north-1.amazonaws.com/snaptoon_web/pre-wed/pre_wed_4.jpg"
  />
);

const babyshowerSteps = [
  {
    title: "Upload Baby Shower Template Photo",
    desc: "Select a baby shower, maternity portrait, or gender reveal template photo.",
  },
  {
    title: "AI Detects Faces",
    desc: "SwapToon's AI automatically identifies faces of mom, dad, or family members in the photo.",
  },
  {
    title: "Upload Family Faces",
    desc: "Upload target face photos for each detected slot (mom-to-be, dad-to-be, family).",
  },
  {
    title: "Generate & Download Photo",
    desc: "Click start swap to create beautiful, festive baby shower photos ready to print or invite!",
  },
];

export const BabyshowerGuide: React.FC = () => (
  <FaceSwapGuide
    title="How to Use Baby Shower Face Swap?"
    steps={babyshowerSteps}
    image="https://snaptoon-development.s3.eu-north-1.amazonaws.com/snaptoon_web/baby-shower/baby_shower_4.jpg"
  />
);
